import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import * as XLSX from "xlsx";
import DashboardMenu from "../commonComponents/DashboardMenu";
import useStyles from "../components/DashboardAuction/dashAuction.style";
import {
  fetchActivityLogFiltersAPI,
  fetchImpersonationLogsAPI,
} from "../../api/activityLogsAPI";

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const formatNameCell = (name, id) => {
  if (name) {
    return name;
  }

  return id || "-";
};

const exportRows = (rows, filename) => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Logs");
  XLSX.writeFile(workbook, filename);
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const timezoneOffset = today.getTimezoneOffset() * 60000;

  return new Date(today.getTime() - timezoneOffset)
    .toISOString()
    .split("T")[0];
};

const buildApiDateTime = (dateValue, boundary) => {
  if (!dateValue) {
    return "";
  }

  return boundary === "end"
    ? `${dateValue}T23:59:59.999`
    : `${dateValue}T00:00:00.000`;
};

const ActivityLogsPage = () => {
  const classes = useStyles();
  const todayDate = getTodayDateInputValue();
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterOptions, setFilterOptions] = useState({
    users: [],
  });
  const [filters, setFilters] = useState({
    userId: "",
    role: "",
    dateFrom: todayDate,
    dateTo: todayDate,
    impersonationOnly: false,
  });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        ...filters,
        dateFrom: buildApiDateTime(filters.dateFrom, "start"),
        dateTo: buildApiDateTime(filters.dateTo, "end"),
        impersonationOnly: String(filters.impersonationOnly),
      };

      const response = await fetchImpersonationLogsAPI(params);
      setRows(response.data?.logs || []);
      setPage(0);
    } catch (requestError) {
      setError("Failed to fetch logs");
    }

    setLoading(false);
  }, [filters]);

  const loadFilterOptions = useCallback(async () => {
    setFiltersLoading(true);

    try {
      const response = await fetchActivityLogFiltersAPI();
      setFilterOptions({
        users: response.data?.users || [],
      });
    } catch (requestError) {
      setError("Failed to load filter options");
    }

    setFiltersLoading(false);
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const displayRows = useMemo(() => {
    const sortedRows = [...rows].sort((firstRow, secondRow) => {
      const firstTime = firstRow.createdAt
        ? new Date(firstRow.createdAt).getTime()
        : 0;
      const secondTime = secondRow.createdAt
        ? new Date(secondRow.createdAt).getTime()
        : 0;

      return secondTime - firstTime;
    });

    return sortedRows.map((row) => ({
      Time: formatDate(row.createdAt),
      Event: row.eventType,
      Action: row.actionName,
      User: formatNameCell(row.userName, row.userId),
      Role: row.role || "-",
      Page: row.page || row.route || "-",
      Entity: [row.entityType, row.entityId].filter(Boolean).join(": ") || "-",
      Outcome: row.outcome || "-",
      Admin: formatNameCell(row.adminName, row.adminUserId),
      Target: formatNameCell(row.targetName, row.targetUserId),
    }));
  }, [rows]);

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return displayRows.slice(startIndex, startIndex + rowsPerPage);
  }, [displayRows, page, rowsPerPage]);

  const formatUserOption = (user) => {
    return `${user.name}${user.role ? ` (${user.role})` : ""}`;
  };

  return (
    <div className={classes.section}>
      <div className="container">
        <div className={classes.Dashboard}>
          <div className={classes.dashboardCard}>
            <DashboardMenu />
            <div className={classes.dashboardCards}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Impersonation &amp; Organiser Admin History
              </Typography>

              <Paper sx={{ p: 2, mb: 2 }}>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 2,
                  }}
                >
                  <TextField
                    select
                    label="User"
                    value={filters.userId}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        userId: event.target.value,
                      }))
                    }
                    disabled={filtersLoading}
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {filterOptions.users.map((user) => (
                      <MenuItem key={user.userId} value={user.userId}>
                        {formatUserOption(user)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Role"
                    value={filters.role}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, role: event.target.value }))
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ADMIN">Admin</MenuItem>
                    <MenuItem value="SELLER">Seller</MenuItem>
                    <MenuItem value="BUYER">Buyer</MenuItem>
                  </TextField>
                  <TextField
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateFrom}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))
                    }
                  />
                  <TextField
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateTo}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, dateTo: event.target.value }))
                    }
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.impersonationOnly}
                        onChange={(event) =>
                          setFilters((prev) => ({
                            ...prev,
                            impersonationOnly: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Impersonation only"
                  />
                  <Button variant="contained" onClick={loadLogs}>
                    Apply Filters
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      exportRows(displayRows, "activity-logs-impersonation.xlsx")
                    }
                  >
                    Export CSV/XLSX
                  </Button>
                </Box>
              </Paper>

              {loading ? (
                <CircularProgress />
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : (
                <Paper sx={{ p: 2, overflowX: "auto" }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {rows.length} records
                  </Typography>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        {Object.keys(displayRows[0] || { Empty: "" }).map((column) => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, index) => (
                        <tr key={`impersonation-${page}-${index}`}>
                          {Object.values(row).map((value, valueIndex) => (
                            <td key={`${index}-${valueIndex}`}>{String(value)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <TablePagination
                    component="div"
                    count={displayRows.length}
                    page={page}
                    onPageChange={(_, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Paper>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogsPage;
